import { useEffect, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, BarChart, Bar } from "recharts";
import { Users, UserPlus, Bot, Headset, Hourglass, Landmark, Home, KeyRound, CalendarCheck, TrendingUp, Timer, RotateCcw } from "lucide-react";
import { api, LEAD_STATUS } from "@/lib/api";
import { PageHeader, Sel } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const COLORS = ["#073B2B", "#D4AF37", "#10B981", "#64748B", "#C5A059", "#0A4D38"];

const Kpi = ({ icon: Icon, label, value, testid, gold }) => (
  <div className="card-lux hover:-translate-y-0.5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <Icon className={`h-4 w-4 ${gold ? "text-accent" : "text-primary dark:text-emerald-400"}`} />
    </div>
    <div className="font-heading text-3xl font-bold mt-3 tracking-tight" data-testid={`${testid}-value`}>{value}</div>
  </div>
);

export default function Dashboard() {
  const [f, setF] = useState({ date_from: "", date_to: "", source: "", property_code: "", broker: "", status: "" });
  const [d, setD] = useState(null);
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  useEffect(() => { api.get("/dashboard", { params: f }).then((r) => setD(r.data)); }, [f]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const k = d?.kpis || {};
  const cards = [
    [Users, "Total de leads", k.total_leads, "kpi-total-leads"], [UserPlus, "Leads hoje", k.leads_today, "kpi-leads-today"],
    [Bot, "Atendimentos pela IA", k.ai_conversations, "kpi-ai-conversations"], [Headset, "Em atendimento humano", k.human_active, "kpi-human-active"],
    [Hourglass, "Aguardando corretor", k.waiting_human, "kpi-waiting-human", true], [Landmark, "Interesse em financiamento", k.financing_interest, "kpi-financing"],
    [Home, "Interesse em compra", k.buy_interest, "kpi-buy"], [KeyRound, "Interesse em locação", k.rent_interest, "kpi-rent"],
    [CalendarCheck, "Visitas agendadas", k.visits_scheduled, "kpi-visits"], [TrendingUp, "Taxa de conversão", `${(k.conversion_rate ?? 0).toString().replace(".", ",")}%`, "kpi-conversion", true],
    [Timer, "Tempo médio de atendimento", `${(k.avg_attendance_minutes ?? 0).toLocaleString("pt-BR")} min`, "kpi-avg-time"],
  ];
  return (
    <div className="animate-in fade-in duration-500">
      <PageHeader title="Dashboard" subtitle="Indicadores calculados a partir dos registros reais da sua imobiliária." />
      <div className="card-lux mb-8 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-3 items-end" data-testid="dashboard-filters">
        <Input type="date" value={f.date_from} onChange={(e) => set("date_from")(e.target.value)} data-testid="filter-date-from" />
        <Input type="date" value={f.date_to} onChange={(e) => set("date_to")(e.target.value)} data-testid="filter-date-to" />
        <Sel value={f.source} onChange={set("source")} placeholder="Todas as origens" options={Object.fromEntries((d?.sources || []).map((s) => [s, s]))} testid="filter-source" />
        <Input placeholder="Código do imóvel" value={f.property_code} onChange={(e) => set("property_code")(e.target.value)} data-testid="filter-property" />
        <Sel value={f.broker} onChange={set("broker")} placeholder="Todos os corretores" options={Object.fromEntries(users.map((u) => [u.id, u.name]))} testid="filter-broker" />
        <Sel value={f.status} onChange={set("status")} placeholder="Todos os status" options={LEAD_STATUS} testid="filter-status" />
        <Button variant="outline" onClick={() => setF({ date_from: "", date_to: "", source: "", property_code: "", broker: "", status: "" })} data-testid="filter-reset"><RotateCcw className="h-4 w-4 mr-2" />Limpar</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
        {cards.map(([icon, label, value, testid, gold]) => <Kpi key={testid} icon={icon} label={label} value={value ?? "—"} testid={testid} gold={gold} />)}
      </div>
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="card-lux lg:col-span-2">
          <h3 className="font-heading font-semibold mb-4">Leads por dia</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={(d?.leads_per_day || []).map((x) => ({ ...x, label: x.date.slice(8, 10) + "/" + x.date.slice(5, 7) }))}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis allowDecimals={false} fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Area type="monotone" dataKey="leads" name="Leads" stroke="#0A4D38" strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-lux">
          <h3 className="font-heading font-semibold mb-4">Origem dos leads</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={d?.by_source || []} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={3}>
                  {(d?.by_source || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-3 text-xs">{(d?.by_source || []).map((s, i) => <span key={s.name} className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{s.name} ({s.value})</span>)}</div>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-lux">
          <h3 className="font-heading font-semibold mb-4">Funil por status</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={(d?.by_status || []).map((s) => ({ ...s, label: LEAD_STATUS[s.name] }))} layout="vertical" margin={{ left: 30 }}>
                <XAxis type="number" allowDecimals={false} fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="label" width={110} fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Bar dataKey="value" name="Leads" fill="#D4AF37" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-lux overflow-x-auto" data-testid="broker-performance">
          <h3 className="font-heading font-semibold mb-4">Desempenho por corretor</h3>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b"><th className="py-2">Corretor</th><th>Leads</th><th>Conversas</th><th>Visitas</th><th>Convertidos</th><th>Taxa</th></tr></thead>
            <tbody>{(d?.brokers || []).map((b) => (
              <tr key={b.id} className="border-b last:border-0"><td className="py-2.5 font-medium">{b.name}</td><td>{b.leads}</td><td>{b.conversations}</td><td>{b.visits}</td><td>{b.converted}</td><td className="font-mono">{b.conversion_rate.toString().replace(".", ",")}%</td></tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
